import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminFindUsContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title: 'Find Us',
    hero_subtitle: 'Visit our premium coworking spaces across Bangalore',
    hero_image_url: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await apiClient.get('/find-us-content');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching find us content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/find-us-content', content);
      toast.success('Find Us section settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Find Us Section Settings</h1>
          <p className="text-gray-600 mt-1">Configure the "Find Us" section header on homepage (Map/Locations section)</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Section Header</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Header Background Image (Optional)</Label>
                <p className="text-sm text-gray-500 mb-2">Upload a background image for the Find Us section. If provided, it will be displayed above the locations map.</p>
                <ImageUpload
                  value={content.hero_image_url}
                  onChange={(url) => setContent({ ...content, hero_image_url: url })}
                  label="Find Us Background Image"
                />
              </div>

              <div>
                <Label>Section Title</Label>
                <Input
                  value={content.hero_title}
                  onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                  placeholder="Find Us"
                  required
                />
              </div>

              <div>
                <Label>Section Subtitle (Optional)</Label>
                <Textarea
                  value={content.hero_subtitle}
                  onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                  placeholder="Visit our premium coworking spaces across Bangalore"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
